import React, { useCallback, useEffect, useState } from "react";
import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import PrivateComponentLoadingPage from "../LoadingPage/PrivateComponentLoadingPage";
import PaymentsAppLayout from "../../Components/Molecules/PaymentsAppLayout";
import { useAdminStore } from "../../Store/Admin";
import { PAGINATION_KEYS, usePagination } from "../../Store/Pagination";
import { AdminTeamView } from "../../Contracts/User";
import Table, { Column, DataSourceItem } from "../../Components/Atoms/Table";
import LoadingPage from "../LoadingPage";
import TablePaginationFooter from "../../Components/Atoms/Table/TablePaginationFooter";
import { toLocalDateString } from "../../Utils/DateUtils";
import { createSortFunction } from "../../Utils/TableUtils";
import { PaymentRoutes } from "../../Contracts/Routes";
import { useDebounce } from "../../Utils/ActionUtils";

const AdminTeamListPage = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [sortParamValue, setSortParamValue] = useState<string>("created_at:desc");

  const { limit, page, setLimit, setPage } = usePagination(PAGINATION_KEYS.ADMIN_TEAM_LIST, {
    limit: 10,
    page: 1
  });

  const { isLoading, teams, teamsCount, getTeams } = useAdminStore();
  const [search, setSearch] = useState<string | undefined>(undefined);
  const debouncedSearch = useDebounce(search, 1500);
  const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value.length > 0) {
      setSearch(event.target.value);
      setPage(1);
    } else {
      setSearch(undefined);
      setPage(1);
    }
  };

  const columns: Column<AdminTeamView>[] = [
    {
      title: "Id",
      dataIndex: "id",
      key: "id"
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: d => (
        <a href={`${PaymentRoutes.ADMIN_TEAMS}/${d.data.id}`} className="text-blue-600">
          {d.data.name}
        </a>
      )
    },
    {
      title: "Billing Email",
      dataIndex: "billing_email",
      key: "billing_email"
    },
    {
      title: "User Count",
      dataIndex: "user_count",
      key: "user_count"
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: d => (
        <span title={toLocalDateString(d.data.created_at, true)}>
          {toLocalDateString(d.data.created_at)}
        </span>
      ),
      sortClicked: createSortFunction("created_at", setSortParamValue),
      defaultSortOrder: "desc"
    }
  ];

  const refreshThings = useCallback(async () => {
    await getTeams(getAccessTokenSilently, limit, sortParamValue, page, undefined, debouncedSearch);
  }, [getTeams, getAccessTokenSilently, limit, sortParamValue, page, debouncedSearch]);

  useEffect(() => {
    refreshThings();
  }, [refreshThings, limit, sortParamValue, page]);

  const dataSource: DataSourceItem<AdminTeamView>[] = teams.map(team => ({
    key: team.id.toString(),
    id: team.id,
    name: team.name,
    billing_email: team.billing_email,
    data: team
  }));

  return (
    <PaymentsAppLayout documentTitle="Admin | Zomentum Payments">
      <div className="flex flex-col h-[calc(100vh-9rem)]">
        <div className="text-xl font-semibold">Partners</div>
        <input
          className="border rounded px-2 py-1 w-60 mt-2"
          placeholder="Email, Name, Or ID"
          value={search || ""}
          onChange={handleSearchInputChange}
        />
        <div className="flex flex-grow flex-col overflow-y-auto">
          {isLoading && teams.length === 0 ? (
            <LoadingPage />
          ) : (
            <Table dataSource={dataSource} columns={columns} isLoading={isLoading} />
          )}
        </div>
        <TablePaginationFooter
          limit={limit}
          setLimit={setLimit}
          setPage={setPage}
          totalRecords={teamsCount}
          page={page}
          isLoading={isLoading}
        />
      </div>
    </PaymentsAppLayout>
  );
};

export default withAuthenticationRequired(AdminTeamListPage, {
  onRedirecting: () => <PrivateComponentLoadingPage />
});
